/**
 * Site Performance — supplier-site monthly OTD scorecard.
 *
 * Supplier-sites-only (the backend returns nothing for customer views). Shows,
 * per site: a 13-month OTD trend, the selected month's headline OTD with RAG
 * colour, and the per-customer breakdown. The current month is in-progress
 * (refreshed nightly); closed months are final/immutable.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { Loader2, AlertCircle, Gauge, Info } from 'lucide-react';

import { performanceAPI, SiteOtdMonthRow, SiteOtdMonthDetail, SiteOtdMatrix } from '../lib/api';
import OtdMissModal from '../components/performance/OtdMissModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { CircularProgress } from '../components/dashboard/SiteKPIComponents';
import { useView } from '../contexts/ViewContext';

const GREEN = '#16a34a';
const AMBER = '#d97706';
const RED = '#dc2626';

interface Thresholds { excellent: number; good: number; poor: number; }
const DEFAULT_THRESHOLDS: Thresholds = { excellent: 90, good: 70, poor: 50 };

function ragColor(pct: number | null, t: Thresholds): string {
  if (pct === null) return '#94a3b8';
  if (pct >= t.excellent) return GREEN;
  if (pct >= t.good) return AMBER;
  return RED;
}

function pctNum(v: string | null): number | null {
  if (v === null || v === undefined) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

export default function SitePerformance() {
  const [rows, setRows] = useState<SiteOtdMonthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [siteId, setSiteId] = useState<number | null>(null);
  const [monthId, setMonthId] = useState<number | null>(null);
  const [detail, setDetail] = useState<SiteOtdMonthDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [matrix, setMatrix] = useState<SiteOtdMatrix | null>(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [view, setView] = useState<'monthly' | 'matrix'>('monthly');

  // OTD-miss drill-down modal (click a sub-100 cell / late count). Bumping
  // refreshTick re-runs the list/detail/matrix fetches after an override.
  const [missModal, setMissModal] = useState<
    { month: string; monthLabel: string; customerId?: number | null; customerName?: string | null } | null
  >(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // The global site/company switcher (top bar) drives this page. For Poeton the
  // site IS the company, so either id can carry the active site.
  const { selectedSiteId, selectedCompanyId, viewKey } = useView();
  const targetSite = selectedSiteId ?? selectedCompanyId ?? null;

  // (Re)load whenever the global scope changes — the backend re-scopes to the
  // active site, so switching sites up top refetches this page.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    performanceAPI
      .listSiteOtd({ months: 13 })
      .then((resp) => {
        if (cancelled) return;
        const data = resp.data || [];
        setRows(data);
        const available = data.map((r) => r.site_id);
        // Prefer the globally-selected site; else the first returned.
        const pick = (targetSite && available.includes(targetSite))
          ? targetSite
          : (data.length ? data[0].site_id : null);
        setSiteId(pick);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load site performance');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey, refreshTick]);

  // Follow the top-bar site switch even if the row set already contains it.
  useEffect(() => {
    if (targetSite && rows.some((r) => r.site_id === targetSite)) {
      setSiteId(targetSite);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSite]);

  // Sites available (distinct, preserving first-seen order).
  const sites = useMemo(() => {
    const seen = new Map<number, string>();
    for (const r of rows) if (!seen.has(r.site_id)) seen.set(r.site_id, r.site_name);
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [rows]);

  // Months for the selected site, newest first.
  const siteMonths = useMemo(
    () => rows
      .filter((r) => r.site_id === siteId)
      .sort((a, b) => b.period_month.localeCompare(a.period_month)),
    [rows, siteId],
  );

  // When the site changes, default to its latest month.
  useEffect(() => {
    if (siteMonths.length) setMonthId(siteMonths[0].id);
    else setMonthId(null);
  }, [siteMonths]);

  // Fetch the per-customer detail for the selected month.
  useEffect(() => {
    if (monthId === null) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    performanceAPI
      .getSiteOtdMonth(monthId)
      .then((resp) => { if (!cancelled) setDetail(resp.data); })
      .catch(() => { if (!cancelled) setDetail(null); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [monthId, refreshTick]);

  // Fetch the rolling 12-month matrix when the site changes.
  useEffect(() => {
    if (siteId === null) { setMatrix(null); return; }
    let cancelled = false;
    setMatrixLoading(true);
    performanceAPI
      .getSiteOtdMatrix({ site: siteId, months: 12 })
      .then((resp) => { if (!cancelled) setMatrix(resp.data); })
      .catch(() => { if (!cancelled) setMatrix(null); })
      .finally(() => { if (!cancelled) setMatrixLoading(false); });
    return () => { cancelled = true; };
  }, [siteId, refreshTick]);

  const thresholds = detail?.thresholds ?? DEFAULT_THRESHOLDS;

  // Trend series, oldest → newest.
  const trend = useMemo(
    () => [...siteMonths].reverse().map((r) => ({
      id: r.id,
      label: r.label.replace(/ \d{4}$/, ''),
      otd: pctNum(r.otd_pct),
      is_closed: r.is_closed,
    })),
    [siteMonths],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="m-6">
        <CardContent className="flex items-center gap-2 py-6 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {error}
        </CardContent>
      </Card>
    );
  }

  if (!rows.length) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Gauge className="h-5 w-5" /> Site Performance
        </h1>
        <Card className="mt-4">
          <CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Info className="h-4 w-4" /> No monthly OTD snapshots yet. They are captured nightly.
          </CardContent>
        </Card>
      </div>
    );
  }

  const selected = siteMonths.find((r) => r.id === monthId) || null;
  const selectedOtd = selected ? pctNum(selected.otd_pct) : null;

  return (
    <div className="p-6 space-y-5">
      {/* Header + site picker */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Gauge className="h-5 w-5" /> Site Performance
          </h1>
          <p className="text-sm text-muted-foreground">
            Monthly On-Time Delivery per site — current month updated nightly, closed months final.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {sites.length > 1 && (
            <Select value={siteId ? String(siteId) : ''} onValueChange={(v) => setSiteId(Number(v))}>
              <SelectTrigger className="w-56"><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {view === 'monthly' && siteMonths.length > 0 && (
            <Select value={monthId ? String(monthId) : ''} onValueChange={(v) => setMonthId(Number(v))}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Month" /></SelectTrigger>
              <SelectContent>
                {siteMonths.map((m) => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    {m.label}{m.is_closed ? '' : ' (live)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <Tabs value={view} onValueChange={(v) => setView(v as 'monthly' | 'matrix')}>
        <TabsList>
          <TabsTrigger value="monthly">Monthly</TabsTrigger>
          <TabsTrigger value="matrix">12-Month Matrix</TabsTrigger>
        </TabsList>

        <TabsContent value="monthly" className="space-y-5 mt-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Selected-month headline */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center justify-between">
              <span>{selected?.label ?? '—'} OTD</span>
              {selected && (
                <Badge variant={selected.is_closed ? 'secondary' : 'default'}>
                  {selected.is_closed ? 'Final' : 'In progress · nightly'}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-4">
            <CircularProgress
              value={selectedOtd ?? 0}
              size={96}
              color={ragColor(selectedOtd, thresholds)}
            />
            <div className="text-xs space-y-1 text-slate-600">
              <div><span className="font-semibold text-slate-900">{selected?.measurable ?? 0}</span> measurable</div>
              <div className="text-emerald-700">{(selected?.on_time ?? 0) + (selected?.early ?? 0)} on-time (incl. {selected?.early ?? 0} early)</div>
              {selected && (selected.late ?? 0) > 0 ? (
                <button
                  className="text-red-700 underline decoration-dotted underline-offset-2 hover:text-red-800"
                  onClick={() => setMissModal({ month: selected.period_month, monthLabel: selected.label })}
                  title="See the late deliveries and override if needed"
                >
                  {selected.late} late
                </button>
              ) : (
                <div className="text-red-700">{selected?.late ?? 0} late</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 13-month trend */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">OTD trend · last 13 months</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 180 }}>
              <ResponsiveContainer>
                <LineChart
                  data={trend}
                  margin={{ top: 5, right: 10, bottom: 0, left: -20 }}
                  onClick={(state: unknown) => {
                    const s = state as { activePayload?: Array<{ payload?: { id?: number } }> };
                    const id = s?.activePayload?.[0]?.payload?.id;
                    if (id) setMonthId(id);
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#cbd5e1' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v: number) => [`${v?.toFixed?.(1) ?? v}%`, 'OTD']} />
                  <ReferenceLine y={thresholds.good} stroke={AMBER} strokeDasharray="4 4" />
                  <ReferenceLine y={thresholds.excellent} stroke={GREEN} strokeDasharray="4 4" />
                  <Line
                    type="monotone" dataKey="otd" stroke="#2563eb" strokeWidth={2}
                    dot={{ r: 3 }} activeDot={{ r: 5 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-customer breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">
            Customer breakdown · {selected?.label ?? '—'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !detail || detail.customers.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">No customer deliveries measured this month.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead className="text-right">Measurable</TableHead>
                  <TableHead className="text-right">On-time</TableHead>
                  <TableHead className="text-right">Early</TableHead>
                  <TableHead className="text-right">Late</TableHead>
                  <TableHead className="text-right">OTD %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.customers.map((c) => {
                  const p = pctNum(c.otd_pct);
                  return (
                    <TableRow key={c.customer_id}>
                      <TableCell className="font-medium">{c.customer_name}</TableCell>
                      <TableCell className="text-muted-foreground">{c.customer_code || '—'}</TableCell>
                      <TableCell className="text-right">{c.measurable}</TableCell>
                      <TableCell className="text-right">{c.on_time}</TableCell>
                      <TableCell className="text-right">{c.early}</TableCell>
                      <TableCell className="text-right">
                        {c.late > 0 && selected ? (
                          <button
                            className="text-red-700 font-medium underline decoration-dotted underline-offset-2 hover:text-red-800"
                            onClick={() => setMissModal({
                              month: selected.period_month, monthLabel: selected.label,
                              customerId: c.customer_id, customerName: c.customer_name,
                            })}
                            title="See which deliveries were late and override if needed"
                          >
                            {c.late}
                          </button>
                        ) : c.late}
                      </TableCell>
                      <TableCell className="text-right font-semibold" style={{ color: ragColor(p, thresholds) }}>
                        {p === null ? '—' : `${p.toFixed(1)}%`}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Rolling 12-month matrix — customers × months, latest month on the left */}
        <TabsContent value="matrix" className="mt-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">
            OTD by customer · rolling 12 months
          </CardTitle>
          <p className="text-[11px] text-muted-foreground">Latest month on the left. Cell = that customer's OTD % that month.</p>
        </CardHeader>
        <CardContent>
          {matrixLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !matrix || matrix.customers.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">No matrix data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="sticky left-0 bg-white text-left font-medium py-2 pr-3 min-w-[180px]">Customer</th>
                    <th className="text-left font-medium py-2 pr-3">Code</th>
                    {matrix.months.map((m) => (
                      <th key={m.period_month} className="text-center font-medium py-2 px-2 whitespace-nowrap">
                        {m.label.replace(/ 20/, " '")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.customers.map((c) => (
                    <tr key={c.customer_id} className="border-b last:border-b-0 hover:bg-slate-50">
                      <td className="sticky left-0 bg-white py-1.5 pr-3 font-medium truncate max-w-[180px]">{c.customer_name}</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">{c.customer_code || '—'}</td>
                      {matrix.month_keys.map((k) => {
                        const v = c.scores[k] ?? null;
                        const clickable = v !== null && v < 100;
                        return (
                          <td
                            key={k}
                            className={`text-center py-1.5 px-2 font-semibold tabular-nums ${clickable ? 'cursor-pointer underline decoration-dotted underline-offset-2 hover:bg-red-50' : ''}`}
                            style={{ color: v === null ? '#cbd5e1' : ragColor(v, thresholds) }}
                            onClick={clickable ? () => setMissModal({
                              month: k,
                              monthLabel: matrix.months.find((m) => m.period_month === k)?.label ?? k,
                              customerId: c.customer_id, customerName: c.customer_name,
                            }) : undefined}
                            title={clickable ? 'See the late deliveries behind this score and override if needed' : undefined}
                          >
                            {v === null ? '·' : v.toFixed(0)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {missModal && siteId !== null && (
        <OtdMissModal
          open
          onClose={() => setMissModal(null)}
          siteId={siteId}
          month={missModal.month}
          monthLabel={missModal.monthLabel}
          customerId={missModal.customerId}
          customerName={missModal.customerName}
          onChanged={() => setRefreshTick((t) => t + 1)}
        />
      )}
    </div>
  );
}
